/**
 * <custom-media-progress> — barra de progreso del slider de medias del PDP.
 *
 * El slider del tema (<hdt-slider>) no expone su API de Embla fuera de
 * global.js, asi que el avance se lee del translate inline que Embla escribe
 * en el track (.hdt-slider__container). Con eso la barra se llena de forma
 * continua durante el swipe, no solo al cambiar de slide.
 *
 * Progreso = (px avanzados + un viewport) / ancho total del track, igual que en
 * custom_gender-media-slider: arranca en 1/N para comunicar que hay mas
 * imagenes y llega a 1 en la ultima.
 */
const TRANSLATE_RE = /translate3d\(\s*(-?[\d.]+)(px|%)/;

class CustomMediaProgress extends HTMLElement {
  connectedCallback() {
    this.slider = document.getElementById(this.getAttribute('slider'));
    if (!this.slider) return;

    this.viewport = this.slider.querySelector('.hdt-slider__viewport');
    this.track = this.slider.querySelector('.hdt-slider__container');
    if (!this.viewport || !this.track) return;

    this.abortController = new AbortController();

    this.trackObserver = new MutationObserver(() => this.schedule());
    this.trackObserver.observe(this.track, {
      attributes: true,
      attributeFilter: ['style', 'hidden'],
      subtree: true,
    });

    this.resizeObserver = new ResizeObserver(() => this.schedule());
    this.resizeObserver.observe(this.viewport);

    this.slider.addEventListener('select:slide', () => this.schedule(), {
      signal: this.abortController.signal,
    });

    this.update();
  }

  disconnectedCallback() {
    if (this.abortController) this.abortController.abort();
    if (this.trackObserver) this.trackObserver.disconnect();
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  schedule() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  }

  update() {
    const total = this.track.scrollWidth;
    const view = this.track.clientWidth;
    const scrollable = total > view + 1;

    this.classList.toggle('custom_media-progress--static', !scrollable);
    if (!scrollable) return;

    const progress = Math.min(1, Math.max(0, (this.offset() + view) / total));
    this.style.setProperty('--custom-mp-progress', progress.toFixed(4));
  }

  offset() {
    const match = TRANSLATE_RE.exec(this.track.style.transform || '');
    if (!match) return 0;
    const value = parseFloat(match[1]);
    return match[2] === '%' ? (-value / 100) * this.track.clientWidth : -value;
  }
}

if (!customElements.get('custom-media-progress')) {
  customElements.define('custom-media-progress', CustomMediaProgress);
}
